/**
 * PapelariaPage.js - Módulo de Serviços de Papelaria
 * CG Business ERP
 * 
 * Gestão de vendas de serviços:
 * - Cópias PB e Cores
 * - Impressões
 * - Scan
 * - Encadernação
 * - Plastificação
 */

import { useEffect, useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { ModulePageHero } from '@/components/common/ModulePageHero';
import { ModuleStateBoundary } from '@/components/common/ModuleStateBoundary';
import { EmptyStatePanel } from '@/components/common/EmptyStatePanel';
import { ResizableTableHandle } from '@/components/common/ResizableTableHandle';
import { safeRequest } from '@/lib/safeRequest';
import { useFetchTelemetry } from '@/hooks/useFetchTelemetry';
import { useResizableTableColumns } from '@/hooks/useResizableTableColumns';
import { CompactPeriodFilters } from '@/components/common/CompactPeriodFilters';
import { COMPACT_ACTION_BUTTONS } from '@/lib/tableDensity';
import { 
  Plus, Minus, Printer, FileText, ScanLine, Receipt, 
  ShoppingCart, Trash2, Clock, DollarSign, Users,
  CreditCard, Banknote, Smartphone
} from 'lucide-react';
import { formatCurrency, cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { useLanguage } from '@/i18n';
import { settingsAPI } from '@/lib/api';
import SalePrintDialog from '@/components/SalePrintDialog';
import { getStoredAuthToken } from '@/lib/authStorage';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: API_BASE_URL });
api.interceptors.request.use(config => {
  const token = getStoredAuthToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Papelaria API
const papelariaAPI = {
  getServices: () => api.get('/api/papelaria/services'),
  getOrders: (params) => api.get('/api/papelaria/orders', { params }),
  createOrder: (data) => api.post('/api/papelaria/orders', data),
  getSummary: (params) => api.get('/api/papelaria/statistics/summary', { params })
};

// Service icons mapping
const SERVICE_ICONS = {
  'Cópia': FileText,
  'Impressão': Printer,
  'Scan': ScanLine,
  'default': FileText
};

const getServiceIcon = (name) => {
  for (const [key, Icon] of Object.entries(SERVICE_ICONS)) {
    if (name?.includes(key)) return Icon;
  }
  return SERVICE_ICONS.default;
};

// Main payment methods (buttons)
const MAIN_PAYMENT_METHODS = ['Numerário', 'M-Pesa', 'E-MOLA'];
const PAPELARIA_TABLE_COLUMN_WIDTHS = {
  number: 88,
  time: 102,
  customer: 148,
  items: 188,
  total: 98,
  payment: 116,
  print: 92,
};

export default function PapelariaPage() {
  // State
  const [services, setServices] = useState([]);
  const [orders, setOrders] = useState([]);
  const [locations, setLocations] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const { getColumnStyle: getPapelariaColumnStyle, startColumnResize: startPapelariaColumnResize } = useResizableTableColumns({
    storageKey: 'cg-papelaria-orders-columns',
    defaultWidths: PAPELARIA_TABLE_COLUMN_WIDTHS,
    minWidth: 72,
  });
  const [summary, setSummary] = useState({ total_orders: 0, total_revenue: 0, total_items: 0 });
  const [orderFilters, setOrderFilters] = useState({ period: 'today', startDate: '', endDate: '' });
  
  // POS state
  const [cart, setCart] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [otherPaymentMethod, setOtherPaymentMethod] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [registeredAt, setRegisteredAt] = useState('');
  const [processing, setProcessing] = useState(false);
  const [printOrder, setPrintOrder] = useState(null);
  const { partialIssues, captureFetchTelemetry, resetFetchTelemetry } = useFetchTelemetry();
  
  const { user } = useAuth();
  const { t } = useLanguage();

  // Separate payment methods into main (buttons) and others (dropdown)
  const mainPayments = useMemo(() => {
    return paymentMethods.filter(pm => 
      MAIN_PAYMENT_METHODS.some(name => pm.name?.toLowerCase().includes(name.toLowerCase()))
    );
  }, [paymentMethods]);

  const otherPayments = useMemo(() => {
    return paymentMethods.filter(pm => 
      !MAIN_PAYMENT_METHODS.some(name => pm.name?.toLowerCase().includes(name.toLowerCase()))
    );
  }, [paymentMethods]);

  // Calculate cart total
  const cartTotal = useMemo(() => {
    return cart.reduce((sum, item) => sum + item.total, 0);
  }, [cart]);

  // Load initial data
  useEffect(() => {
    loadData();
  }, [orderFilters]);

  const loadData = async () => {
    try {
      setLoading(true);
      resetFetchTelemetry();
      const queryParams = {
        period: orderFilters.period,
        ...(orderFilters.startDate ? { start_date: orderFilters.startDate } : {}),
        ...(orderFilters.endDate ? { end_date: orderFilters.endDate } : {}),
      };
      const [servicesRes, ordersRes, locationsRes, employeesRes, paymentRes, summaryRes] = await Promise.all([
        safeRequest(() => papelariaAPI.getServices(), { data: [] }, { telemetry: { module: 'papelaria', endpoint: 'papelaria.services', onEvent: captureFetchTelemetry } }),
        safeRequest(() => papelariaAPI.getOrders(queryParams), { data: [] }, { telemetry: { module: 'papelaria', endpoint: 'papelaria.orders', onEvent: captureFetchTelemetry } }),
        safeRequest(() => settingsAPI.listActiveLocations(), { data: [] }, { telemetry: { module: 'papelaria', endpoint: 'settings.locations', onEvent: captureFetchTelemetry } }),
        safeRequest(() => settingsAPI.listActiveEmployees(), { data: [] }, { telemetry: { module: 'papelaria', endpoint: 'settings.employees', onEvent: captureFetchTelemetry } }),
        safeRequest(() => settingsAPI.listPaymentMethods(), { data: [] }, { telemetry: { module: 'papelaria', endpoint: 'settings.payment_methods', onEvent: captureFetchTelemetry } }),
        safeRequest(() => papelariaAPI.getSummary(queryParams), { data: { total_orders: 0, total_revenue: 0, total_items: 0 } }, { telemetry: { module: 'papelaria', endpoint: 'papelaria.summary', onEvent: captureFetchTelemetry } })
      ]);
      
      setServices(servicesRes.data || []);
      setOrders(ordersRes.data || []);
      setLocations(locationsRes.data || []);
      setEmployees(employeesRes.data || []);
      setPaymentMethods(paymentRes.data || []);
      setSummary(summaryRes.data || { total_orders: 0, total_revenue: 0, total_items: 0 });
      
      // Auto-select first store location
      const storeLocation = locationsRes.data?.find(l => l.type === 'store');
      if (!selectedLocation && storeLocation) setSelectedLocation(storeLocation);
      
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };
  const hasPapelariaData = Boolean(
    services.length ||
    orders.length ||
    locations.length ||
    employees.length ||
    paymentMethods.length
  );

  // Add service to cart
  const addToCart = (service) => {
    const existing = cart.find(item => item.service_id === service.id);
    
    if (existing) {
      setCart(cart.map(item => 
        item.service_id === service.id 
          ? { ...item, quantity: item.quantity + 1, total: (item.quantity + 1) * item.unit_price }
          : item
      ));
    } else {
      setCart([...cart, {
        service_id: service.id,
        service_name: service.name,
        quantity: 1,
        unit_price: service.unit_price || 0,
        total: service.unit_price || 0
      }]);
    }
    
    toast.success(`${service.name} adicionado`, { duration: 1500 });
  };

  // Update quantity
  const updateQuantity = (serviceId, delta) => {
    setCart(cart.map(item => {
      if (item.service_id === serviceId) {
        const newQty = Math.max(1, item.quantity + delta);
        return { ...item, quantity: newQty, total: newQty * item.unit_price };
      }
      return item;
    }));
  };

  // Remove from cart
  const removeFromCart = (serviceId) => {
    setCart(cart.filter(item => item.service_id !== serviceId));
  };

  // Process order
  const handleProcessOrder = async () => {
    if (cart.length === 0) {
      toast.error('Carrinho vazio');
      return;
    }
    if (!selectedLocation) {
      toast.error('Seleccione uma localização');
      return;
    }
    if (!selectedEmployee) {
      toast.error('Seleccione um funcionário');
      return;
    }
    if (!selectedPayment) {
      toast.error('Seleccione um método de pagamento');
      return;
    }
    
    setProcessing(true);
    try {
      await papelariaAPI.createOrder({
        items: cart,
        customer_name: customerName || null,
        location_id: selectedLocation.id,
        employee_id: selectedEmployee,
        payment_method_id: selectedPayment,
        registered_at: registeredAt ? new Date(registeredAt).toISOString() : null
      });
      
      toast.success(`Venda registada! Total: ${formatCurrency(cartTotal)}`);
      
      // Reset form
      setCart([]);
      setCustomerName('');
      setRegisteredAt('');
      loadData();
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao processar venda');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="p-3 space-y-3" data-testid="papelaria-page">
      <ModulePageHero
        testId="papelaria-hero"
        title={t('papelariaPage.title')}
        description=""
        icon={Printer}
        badge={t('papelariaPage.badge')}
        compact
        stats={[
          { label: t('papelariaPage.stats.salesToday'), value: summary.total_orders || 0 },
          { label: t('papelariaPage.stats.revenueToday'), value: formatCurrency(summary.total_revenue || 0) },
          { label: t('papelariaPage.stats.location'), value: selectedLocation?.name || '—' },
          { label: t('papelariaPage.stats.employee'), value: selectedEmployee?.name || t('papelariaPage.freeLabel') },
        ]}
      />

      <ModuleStateBoundary
        loading={loading}
        hasData={hasPapelariaData}
        partialIssues={partialIssues}
        loadingTestId="papelaria-loading-state"
        partialIssuesTestId="papelaria-partial-issues"
        emptyState={(
          <EmptyStatePanel
            title={t('papelariaPage.emptyTitle')}
            description={t('papelariaPage.emptyDescription')}
            ctaLabel={t('papelariaPage.emptyCta')}
            ctaTo="/settings"
            testId="papelaria-empty-state"
          />
        )}
      >

      <Card className="border-slate-200 shadow-sm" data-testid="papelaria-operations-strip">
        <CardContent className="flex flex-wrap items-end gap-2 p-2.5">
          <div className="min-w-[200px] flex-1">
            <Label className="text-[10px] uppercase tracking-[0.14em] text-slate-500">{t('papelariaPage.stats.location')}</Label>
            <Select 
              value={selectedLocation?.id || ''} 
              onValueChange={(v) => setSelectedLocation(locations.find(l => l.id === v))}
            >
              <SelectTrigger className="mt-1 h-8 rounded-full text-[11px]" data-testid="papelaria-location-select">
                <SelectValue placeholder={t('papelariaPage.selectPlaceholder')} />
              </SelectTrigger>
              <SelectContent>
                {locations.filter(l => l.type === 'store').map(loc => (
                  <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[200px] flex-1">
            <Label className="text-[10px] uppercase tracking-[0.14em] text-slate-500">{t('papelariaPage.stats.employee')}</Label>
            <Select value={selectedEmployee || ''} onValueChange={setSelectedEmployee}>
              <SelectTrigger className="mt-1 h-8 rounded-full text-[11px]" data-testid="papelaria-employee-select">
                <SelectValue placeholder={t('papelariaPage.selectPlaceholder')} />
              </SelectTrigger>
              <SelectContent>
                {employees.map(emp => (
                  <SelectItem key={emp.id} value={emp.id}>{emp.employee_number ? `${emp.employee_number} · ${emp.name}` : emp.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <CompactPeriodFilters
            period={orderFilters.period}
            startDate={orderFilters.startDate}
            endDate={orderFilters.endDate}
            onPeriodChange={(value) => setOrderFilters((current) => ({ ...current, period: value }))}
            onStartDateChange={(value) => setOrderFilters((current) => ({ ...current, startDate: value }))}
            onEndDateChange={(value) => setOrderFilters((current) => ({ ...current, endDate: value }))}
            periodTestId="papelaria-period-select"
            startDateTestId="papelaria-start-date-filter"
            endDateTestId="papelaria-end-date-filter"
          />
          <div className="flex flex-wrap gap-1.5" data-testid="papelaria-operations-metrics">
            <Badge variant="outline" className="rounded-full border-slate-200 bg-slate-50 text-[10px] text-slate-700">
              <Receipt className="mr-1 h-3 w-3" /> {summary.total_orders} vendas
            </Badge>
            <Badge variant="outline" className="rounded-full border-slate-200 bg-slate-50 text-[10px] text-slate-700">
              <DollarSign className="mr-1 h-3 w-3" /> {formatCurrency(summary.total_revenue)}
            </Badge>
            <Badge variant="outline" className="rounded-full border-slate-200 bg-slate-50 text-[10px] text-slate-700">
              {summary.total_items || 0} itens hoje
            </Badge>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-3 xl:grid-cols-[minmax(0,1fr)_300px]">
        {/* Services Grid */}
        <div>
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="px-3 py-2">
              <CardTitle className="text-sm font-semibold">{t('papelariaPage.availableServices')}</CardTitle>
            </CardHeader>
            <CardContent className="p-2.5 pt-0">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : (
                <div className="grid gap-1.5" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(112px, 1fr))' }}>
                  {services.map(service => {
                    const ServiceIcon = getServiceIcon(service.name);
                    return (
                      <Button
                        key={service.id}
                        variant="outline"
                        title={service.name}
                        className="h-auto min-h-[62px] flex-col items-start justify-start gap-0.5 rounded-xl border-slate-200 px-2 py-1.5 hover:bg-blue-50 hover:border-blue-300"
                        onClick={() => addToCart(service)}
                        data-testid={`papelaria-service-card-${service.id}`}
                      >
                        <div className="mb-1 flex w-full items-center justify-between gap-1">
                          <ServiceIcon className="h-3.5 w-3.5 text-blue-600" />
                          <span className="inline-flex h-4 rounded-full bg-slate-100 px-1.5 text-[11px] font-semibold leading-4 text-slate-700">
                            {formatCurrency(service.unit_price || 0)}
                          </span>
                        </div>
                        <span className="w-full whitespace-normal break-words text-left text-[11.5px] font-medium leading-4">{service.name}</span>
                      </Button>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Cart / Checkout */}
        <div>
          <Card className="sticky top-4 border-slate-200 shadow-sm">
            <CardHeader className="px-3 py-2.5">
              <CardTitle className="flex items-center gap-2 text-sm font-semibold">
                <ShoppingCart className="h-5 w-5" />
                {t('papelariaPage.cartTitle', { count: cart.length })}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 p-3 pt-0">
              {cart.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  {t('papelariaPage.emptyCart')}
                </p>
              ) : (
                <div className="space-y-2 max-h-[280px] overflow-y-auto">
                  {cart.map(item => (
                    <div key={item.service_id} className="grid grid-cols-[minmax(0,1fr)_110px_72px] items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-2">
                      <div className="flex-1 min-w-0">
                        <p className="truncate text-xs font-medium">{item.service_name}</p>
                        <p className="text-[10px] text-muted-foreground">
                          {formatCurrency(item.unit_price)} × {item.quantity}
                        </p>
                      </div>
                      <div className="flex items-center justify-center gap-1">
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="h-7 w-7 p-0"
                          onClick={() => updateQuantity(item.service_id, -1)}
                        >
                          <Minus className="h-3 w-3" />
                        </Button>
                        <span className="w-8 text-center text-xs font-semibold">{item.quantity}</span>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="h-7 w-7 p-0"
                          onClick={() => updateQuantity(item.service_id, 1)}
                        >
                          <Plus className="h-3 w-3" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="h-7 w-7 p-0 text-red-500 hover:text-red-700"
                          onClick={() => removeFromCart(item.service_id)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                      <p className="w-16 text-right text-xs font-semibold text-slate-900">
                        {formatCurrency(item.total)}
                      </p>
                    </div>
                  ))}
                </div>
              )}

              <Separator />

              {/* Customer Name (optional) */}
              <div className="space-y-1">
                <Label className="text-[10px] uppercase tracking-[0.14em] text-slate-500">{t('papelariaPage.customerOptional')}</Label>
                <Input 
                  placeholder={t('papelariaPage.customerPlaceholder')}
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  className="h-8 text-sm"
                />
              </div>

              {/* Payment Method */}
              <div className="space-y-1">
                <Label className="text-[10px] uppercase tracking-[0.14em] text-slate-500">{t('papelariaPage.payment')}</Label>
                <div className="flex gap-1 flex-wrap items-center">
                  {/* Main payment buttons */}
                  {mainPayments.map(pm => (
                    <Button
                      key={pm.id}
                      size="sm"
                      variant={selectedPayment === pm.id ? "default" : "outline"}
                      className={cn("h-8 text-xs", selectedPayment === pm.id && "bg-blue-600 hover:bg-blue-700")}
                      onClick={() => {
                        setSelectedPayment(pm.id);
                        setOtherPaymentMethod('');
                      }}
                    >
                      {pm.name?.toLowerCase().includes('cash') || pm.name?.toLowerCase().includes('numerário') ? 
                        <Banknote className="h-3 w-3 mr-1" /> : 
                       pm.name?.toLowerCase().includes('m-pesa') ? <Smartphone className="h-3 w-3 mr-1" /> :
                       pm.name?.toLowerCase().includes('e-mola') ? <Smartphone className="h-3 w-3 mr-1" /> :
                       <CreditCard className="h-3 w-3 mr-1" />}
                      {pm.name}
                    </Button>
                  ))}
                  
                  {/* Dropdown for other payment methods */}
                  {otherPayments.length > 0 && (
                    <Select 
                      value={otherPaymentMethod} 
                      onValueChange={(v) => {
                        setOtherPaymentMethod(v);
                        setSelectedPayment(v);
                      }}
                    >
                      <SelectTrigger className={cn(
                        "h-8 w-[120px] text-xs",
                        otherPaymentMethod && "border-blue-400"
                      )}>
                        <SelectValue placeholder={t('papelariaPage.otherPayments')} />
                      </SelectTrigger>
                      <SelectContent>
                        {otherPayments.map(pm => (
                          <SelectItem key={pm.id} value={pm.id}>
                            {pm.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              </div>

              {/* Date/Time Override */}
              <div className="space-y-1">
                <Label className="text-[10px] uppercase tracking-[0.14em] text-slate-500 flex items-center gap-1">
                  <Clock className="h-3 w-3 text-amber-500" />
                  {t('papelariaPage.datetimeOptional')}
                </Label>
                <Input 
                  type="datetime-local"
                  value={registeredAt}
                  onChange={(e) => setRegisteredAt(e.target.value)}
                  max={new Date().toISOString().slice(0, 16)}
                  className="h-8 text-sm border-amber-200"
                />
              </div>

              <Separator />

              {/* Total and Checkout */}
              <div className="space-y-2 rounded-xl border border-slate-200 bg-slate-50 p-3">
                <div className="flex justify-between items-center text-sm font-semibold uppercase tracking-[0.14em] text-slate-500">
                  <span>{t('papelariaPage.total')}</span>
                  <span className="text-base font-bold text-green-600">{formatCurrency(cartTotal)}</span>
                </div>
                
                <Button 
                  className="h-10 w-full bg-green-600 text-sm hover:bg-green-700"
                  onClick={handleProcessOrder}
                  disabled={cart.length === 0 || processing}
                  data-testid="papelaria-process-order-button"
                >
                  {processing ? t('papelariaPage.processing') : t('papelariaPage.finishSale')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Recent Orders */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="px-3 py-2.5">
          <CardTitle className="text-sm font-semibold">{t('papelariaPage.recentSales')}</CardTitle>
        </CardHeader>
        <CardContent className="p-3 pt-0">
          {orders.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              {t('papelariaPage.noRecentSales')}
            </p>
          ) : (
            <div className="overflow-x-auto max-h-[420px]">
              <table className="w-full text-[11px] leading-tight">
                <thead className="sticky top-0 z-10 border-b bg-slate-50/95 backdrop-blur">
                  <tr>
                    <th className="relative px-2 py-1.5 text-left text-[10px] font-medium" style={getPapelariaColumnStyle('number')}>Nº<ResizableTableHandle onMouseDown={(event) => startPapelariaColumnResize('number', event)} testId="papelaria-column-resize-number" /></th>
                    <th className="relative px-2 py-1.5 text-left text-[10px] font-medium" style={getPapelariaColumnStyle('time')}>Hora<ResizableTableHandle onMouseDown={(event) => startPapelariaColumnResize('time', event)} testId="papelaria-column-resize-time" /></th>
                    <th className="relative px-2 py-1.5 text-left text-[10px] font-medium" style={getPapelariaColumnStyle('customer')}>Cliente<ResizableTableHandle onMouseDown={(event) => startPapelariaColumnResize('customer', event)} testId="papelaria-column-resize-customer" /></th>
                    <th className="relative px-2 py-1.5 text-left text-[10px] font-medium" style={getPapelariaColumnStyle('items')}>Itens<ResizableTableHandle onMouseDown={(event) => startPapelariaColumnResize('items', event)} testId="papelaria-column-resize-items" /></th>
                    <th className="relative px-2 py-1.5 text-right text-[10px] font-medium" style={getPapelariaColumnStyle('total')}>Total<ResizableTableHandle onMouseDown={(event) => startPapelariaColumnResize('total', event)} testId="papelaria-column-resize-total" /></th>
                    <th className="relative px-2 py-1.5 text-left text-[10px] font-medium" style={getPapelariaColumnStyle('payment')}>Pagamento<ResizableTableHandle onMouseDown={(event) => startPapelariaColumnResize('payment', event)} testId="papelaria-column-resize-payment" /></th>
                    <th className="relative px-2 py-1.5 text-right text-[10px] font-medium" style={getPapelariaColumnStyle('print')}>Impr.<ResizableTableHandle onMouseDown={(event) => startPapelariaColumnResize('print', event)} testId="papelaria-column-resize-print" /></th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {orders.slice(0, 10).map(order => (
                    <tr key={order.id} className="hover:bg-slate-50/50">
                      <td className="px-2 py-1.5 font-mono text-[11px]" style={getPapelariaColumnStyle('number')}>{order.order_number}</td>
                      <td className="px-2 py-1.5 text-[11px]" style={getPapelariaColumnStyle('time')}>
                        {new Date(order.created_at).toLocaleTimeString('pt-MZ', { hour: '2-digit', minute: '2-digit' })}
                      </td>
                      <td className="px-2 py-1.5 text-[11px]" style={getPapelariaColumnStyle('customer')}>{order.customer_name || '-'}</td>
                      <td className="px-2 py-1.5 text-[11px]" style={getPapelariaColumnStyle('items')}>{order.items?.length || 0} itens</td>
                      <td className="px-2 py-1.5 text-right text-[11px] font-medium" style={getPapelariaColumnStyle('total')}>{formatCurrency(order.total)}</td>
                      <td className="px-2 py-1.5 text-[10px]" style={getPapelariaColumnStyle('payment')}>{order.payment_method_name}</td>
                      <td className="px-2 py-1.5 text-right" style={getPapelariaColumnStyle('print')}>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className={COMPACT_ACTION_BUTTONS.tab}
                          onClick={() => setPrintOrder(order)}
                          disabled={!order.sale_id}
                          data-testid={`papelaria-order-print-button-${order.id}`}
                        >
                          <Printer className="mr-1 h-3.5 w-3.5" />
                          Impr.
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <SalePrintDialog
        open={Boolean(printOrder)}
        onOpenChange={(open) => {
          if (!open) setPrintOrder(null);
        }}
        sale={printOrder ? {
          id: printOrder.sale_id,
          sale_number: printOrder.order_number,
          payment_method_name: printOrder.payment_method_name,
          total_amount: printOrder.total,
          payments: [{ amount: printOrder.total, payment_method_name: printOrder.payment_method_name }],
          location_name: printOrder.location_name,
          location_id: printOrder.location_id,
        } : null}
      />
      </ModuleStateBoundary>
    </div>
  );
}
